import { UpperCasePipe } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
	BadgeComponent,
	CardSelectComponent,
	CollapseComponent,
	DividerComponent,
	InfoGroupComponent,
	RateStatusComponent,
	TagComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { IPolOperation, OPERATION_TYPE, POL_TEST } from '../../../../../operations/models/operations.models';
import { CONFIDENCE_THRESHOLD, DEMAND_LEVEL, IPolDemandLevel } from '../../../../models/workflows.models';
import { WorkflowsHelperService } from '../../../../services/workflows-helper.service';
import { NOT_AVAILABLE_DISPLAY } from '../../../../../core/utils/utils';
import { DeveloperCurlButtonComponent } from '../../../../../shared/components/developer-curl-button/developer-curl-button.component';

@Component({
	selector: 'app-pol-operation',
	imports: [
		TranslatePipe,
		UpperCasePipe,
		TileComponent,
		CollapseComponent,
		InfoGroupComponent,
		DividerComponent,
		TagComponent,
		BadgeComponent,
		RateStatusComponent,
		CardSelectComponent,
		DeveloperCurlButtonComponent,
	],
	templateUrl: './pol-operation.component.html',
	styleUrl: './pol-operation.component.scss',
})
export class PolOperationComponent implements OnInit {
	@Input() operation?: IPolOperation;
	@Input() index: number = 1;
	@Input() profileUuid?: string;

	public workflowsHelper = inject(WorkflowsHelperService);

	public POL_TEST = POL_TEST;
	public CONFIDENCE_THRESHOLD = CONFIDENCE_THRESHOLD;
	public demandLevelLabel = 'WORKFLOWS.FORM.OPERATIONS.CUSTOM';
	private readonly POL_SCORING_MAX = 7;
	private readonly POL_SCORING_STANDARD = 6.8;
	private readonly POL_SCORING_EPSILON = 0.0001;

	ngOnInit(): void {
		if (this.operation && this.operation.type !== OPERATION_TYPE.POL) {
			this.operation = undefined;
		} else {
			this.updateDemandLevelLabel();
		}
	}

	public operationCurlPath(): string {
		if (!this.profileUuid || !this.operation?.uuid) return '';
		return `/profile/${this.profileUuid}/operations/${this.operation.uuid}`;
	}

	private updateDemandLevelLabel(): void {
		if (!this.operation) return;

		const demandLevel = this.workflowsHelper.selectPolDemandLevel(this.operation.config.setting);

		switch (demandLevel) {
			case DEMAND_LEVEL.STANDARD:
				this.demandLevelLabel = 'WORKFLOWS.FORM.OPERATIONS.STANDARD';
				break;
			case DEMAND_LEVEL.MAX_FLUID:
				this.demandLevelLabel = 'WORKFLOWS.FORM.OPERATIONS.MAX_FLUID';
				break;
			case DEMAND_LEVEL.CUSTOM:
			default:
				this.demandLevelLabel = 'WORKFLOWS.FORM.OPERATIONS.CUSTOM';
				break;
		}
	}

	public isCustomConfidenceThreshold(): boolean {
		const scoring = this.getPolScoring();
		if (scoring === null) return false;
		return (
			Math.abs(scoring - this.POL_SCORING_MAX) > this.POL_SCORING_EPSILON &&
			Math.abs(scoring - this.POL_SCORING_STANDARD) > this.POL_SCORING_EPSILON
		);
	}

	public getConfidenceThresholdLabelKey(): string {
		if (this.isCustomConfidenceThreshold()) {
			return 'WORKFLOWS.FORM.OPERATIONS.CUSTOM';
		}
		return `WORKFLOWS.FORM.OPERATIONS.CONFIDENCE_THRESHOLD_OPTIONS.${
			this.workflowsHelper.getScoring(this.operation?.config?.setting) === CONFIDENCE_THRESHOLD.MAX
				? 'HIGH'
				: 'STANDARD'
		}`;
	}

	public getCustomConfidenceThresholdDisplay(): string {
		const scoring = this.getPolScoring();
		if (scoring === null) return NOT_AVAILABLE_DISPLAY;
		return scoring.toFixed(1).replace('.', ',');
	}

	private getPolScoring(): number | null {
		const rawScoring = this.operation?.config?.setting?.scoring;
		const normalized = String(rawScoring ?? '').trim().replace(',', '.');
		const parsed = Number(normalized);
		return Number.isFinite(parsed) ? parsed : null;
	}
}
